import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';

const CuentasBancarias = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const cuentasData = [
    { id: 1, banco: 'Banco Nación', tipo: 'Cuenta Corriente', numero: '****4521', moneda: 'ARS', saldo: 8500000, activa: true },
    { id: 2, banco: 'Banco Galicia', tipo: 'Cuenta Corriente', numero: '****7832', moneda: 'ARS', saldo: 4200000, activa: true },
    { id: 3, banco: 'Banco Macro', tipo: 'Caja de Ahorro', numero: '****1245', moneda: 'USD', saldo: 125000, activa: true },
    { id: 4, banco: 'Santander', tipo: 'Cuenta Corriente', numero: '****9087', moneda: 'ARS', saldo: 2500000, activa: true },
  ];

  const movimientosRecientes = [
    { fecha: '03/02/2026', concepto: 'Cobro Factura #1234', cuenta: 'Banco Nación', tipo: 'Ingreso', monto: 2500000 },
    { fecha: '02/02/2026', concepto: 'Pago Proveedor #4521', cuenta: 'Banco Galicia', tipo: 'Egreso', monto: 1250000 },
    { fecha: '01/02/2026', concepto: 'Transferencia interna', cuenta: 'Banco Macro', tipo: 'Egreso', monto: 50000 },
    { fecha: '31/01/2026', concepto: 'Cobro Factura #1198', cuenta: 'Santander', tipo: 'Ingreso', monto: 1800000 },
  ];

  const totalARS = cuentasData.filter(c => c.moneda === 'ARS').reduce((sum, c) => sum + c.saldo, 0);
  const totalUSD = cuentasData.filter(c => c.moneda === 'USD').reduce((sum, c) => sum + c.saldo, 0);

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Cuentas Bancarias
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de cuentas y saldos
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nueva Cuenta
        </Button>
      </Box>

      {/* Totales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BankIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total en Pesos (ARS)</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    ${totalARS.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BankIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total en Dólares (USD)</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    USD {totalUSD.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Cuentas */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Cuentas Activas
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Banco</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuentasData.map((cuenta) => (
                      <TableRow key={cuenta.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{cuenta.banco}</Typography>
                          <Typography variant="caption" color="text.secondary">{cuenta.numero}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={cuenta.tipo} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {cuenta.moneda === 'USD' ? 'USD ' : '$'}{cuenta.saldo.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Movimientos Recientes */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Movimientos Recientes
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientosRecientes.map((mov, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{mov.fecha}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{mov.concepto}</Typography>
                          <Typography variant="caption" color="text.secondary">{mov.cuenta}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {mov.tipo === 'Ingreso' ? (
                              <TrendingUpIcon fontSize="small" color="success" />
                            ) : (
                              <TrendingDownIcon fontSize="small" color="error" />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={mov.tipo === 'Ingreso' ? 'success.main' : 'error.main'}
                            >
                              ${mov.monto.toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Cuenta Bancaria</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Banco" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Cuenta</InputLabel>
                <Select label="Tipo de Cuenta">
                  <MenuItem value="cc">Cuenta Corriente</MenuItem>
                  <MenuItem value="ca">Caja de Ahorro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select label="Moneda">
                  <MenuItem value="ARS">Pesos (ARS)</MenuItem>
                  <MenuItem value="USD">Dólares (USD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Número de Cuenta" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="CBU" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Alias" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CuentasBancarias;
